// src/components/billing/PlanCard.jsx — one row of the plans screen.
//
// ⛔⛔ THIS COMPONENT DECIDES NOTHING. The backend sends `action` (which button)
// and `reason_code` (why there is none). Deriving either from price, plan id or
// dates is exactly what the contract forbids — the previous screen did that and
// offered buttons the server then refused.
//
// ⚠️ `price` is NET of VAT. Label it "+ IVA"; Stripe shows the VAT line and the
// total on its own page. ⛔ No VAT arithmetic here.
//
// `isCurrent` is a prop: the caller derives it from the response's TOP-LEVEL
// plan_id. `billingDetailsTo` (default /billing-details) is where an
// `unavailable` row with CUSTOMER_NOT_INVOICEABLE links — the Gestione Workspace
// tab points it at its own "Dati azienda" tab.
//
// ⭐ v203: an unavailable row is worded from `reason_code` (billing/refusalCopy.js :: rowReasonCopy) — ⛔ never the
// row's English `reason`; the current plan (PLAN_UNCHANGED) says nothing, its badge already does. While the page
// is busy the button keeps its own label (disabled) — no «Reindirizzamento…».
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { formatCredits, formatPrice } from '../../utils/creditsFormat.js';
import { rowReasonCopy } from '../../billing/refusalCopy.js';
import { COLORS, FONT_SANS, RADII } from '../../constants/theme.js';

/** Rows nobody can buy are hidden; the rest grey with their reason. */
export const HIDDEN_REASON_CODES = new Set(['PLAN_NOT_SELLABLE', 'PLAN_NOT_PURCHASABLE']);

const ACTION_LABEL = {
  'start-subscription': 'plan.subscribe',
  'change-now': 'plan.changeNow',
  'change-at-renewal': 'plan.changeAtRenewal',
};

// `title` (optional): the grid names a card after its FAMILY (the toggles already say the period and the workspaces);
// without it the row's own `display_name` is shown (loose rows, flat fallback).
export default function PlanCard({ option, isCurrent, onAct, busy = false, billingDetailsTo = '/billing-details', title }) {
  const { t } = useTranslation('billing');
  const actionable = option.action !== 'unavailable';
  // ⭐ 2026-10-08: the plan IN FORCE offered as a first purchase (an operator-assigned plan, no Stripe subscription)
  // says «Attiva il pagamento» — «Acquista» on the card marked «Abbonamento attuale» read as buying what you already have.
  const labelKey = isCurrent && option.action === 'start-subscription' ? 'plan.activatePayment' : ACTION_LABEL[option.action];

  return (
    <div style={{
      background: COLORS.surface, border: `1px solid ${isCurrent ? COLORS.accent : COLORS.border}`,
      borderRadius: RADII.control, padding: '18px 20px', fontFamily: FONT_SANS,
      opacity: actionable ? 1 : 0.6,
    }}>
      {/* ⭐ 2026-10-08: the plan NAME is the big, centred line; the price below is the smaller one, centred too. */}
      <h3 style={{ fontSize: 22, fontWeight: 700, margin: 0, textAlign: 'center' }}>{title || option.display_name}</h3>
      {isCurrent && (
        <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.accent, textAlign: 'center', marginTop: 2 }}>
          {t('plan.current')}
        </div>
      )}

      {/* ⚠️ price is a Decimal STRING and NET of VAT — never computed on. ⛔ Shown WITHOUT
          decimals (`formatPrice`, 2026-09-29): the column is NUMERIC(18,6), so the wire says
          "59.000000"; the currency code is shown as sent.
          ⛔ The per-period unit comes from `billing_interval` and is OMITTED when that is
          null. `free` is a 14-day NON-RENEWING trial whose period is not in the backend's
          {30:'month', 365:'year'} map, and the old hard-coded month fallback printed
          "0 EUR / mese" — inventing a billing period the plan does not have. */}
      {option.price != null && (
        <div style={{ marginTop: 10, fontSize: 16, fontWeight: 700, textAlign: 'center' }}>
          {formatPrice(option.price) ?? option.price} {option.currency}
          <span style={{ fontSize: 12, fontWeight: 500, color: COLORS.textSecondary }}>
            {option.billing_interval
              ? ` / ${t(`interval.${option.billing_interval}Short`, { defaultValue: option.billing_interval })} `
              : ' '}
            {t('plan.vatExcluded')}
          </span>
        </div>
      )}

      {option.allowance_credits != null && (
        <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 6 }}>
          {t('plan.credits', { credits: formatCredits(option.allowance_credits) })}
        </div>
      )}

      {/* ⭐ The caps are what the workspace toggle BUYS — without them the two positions
          differ only by price. ⛔ null = UNLIMITED by contract, never "unknown". */}
      <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>
        {option.max_tenants != null
          ? t('plan.workspaces', { n: option.max_tenants })
          : t('plan.workspacesUnlimited')}
      </div>
      <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 2 }}>
        {option.max_users != null
          ? t('plan.seats', { n: option.max_users })
          : t('plan.seatsUnlimited')}
      </div>

      {/* ⭐ `renews` is the ONLY thing separating a trial from a paid plan (the backend
          seed says so). ⛔ STRICTLY false — `null` is "not told", not "a trial". ⛔ No day
          count: `period_days` is not a labelling field. */}
      {option.renews === false && (
        <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.accent, marginTop: 6 }}>
          {t('plan.trial')}
        </div>
      )}

      {/* ⛔ OUR sentence from `reason_code` — never the row's English `reason`. The current plan
          (PLAN_UNCHANGED) says nothing: the «Abbonamento attuale» badge already does. */}
      {!actionable && (() => {
        const text = rowReasonCopy(t, option.reason_code);
        if (!text && option.reason_code !== 'CUSTOMER_NOT_INVOICEABLE') return null;
        return (
          <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 10 }}>
            {text}
            {option.reason_code === 'CUSTOMER_NOT_INVOICEABLE' && (
              <> <Link to={billingDetailsTo} style={{ color: COLORS.accent, fontWeight: 600 }}>
                {t('plan.completeInvoiceDetails')}
              </Link></>
            )}
          </div>
        );
      })()}

      {actionable && (
        <button type="button" disabled={busy} onClick={() => onAct(option)}
          style={{
            marginTop: 14, width: '100%', padding: '10px', border: 'none',
            borderRadius: RADII.controlInner, background: busy ? COLORS.borderStrong : COLORS.accent,
            color: COLORS.surface, fontSize: 14, fontWeight: 600,
            cursor: busy ? 'not-allowed' : 'pointer', fontFamily: FONT_SANS,
          }}>
          {t(labelKey)}
        </button>
      )}
    </div>
  );
}
